import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Calendar, Plus, Edit, Trash2, Loader2, Users, Receipt, Eye, AlertTriangle, RotateCcw, ArrowLeft, Download, Mail, Wallet, UserPlus, Printer, ArrowUp, ArrowDown, ChevronsUpDown, X } from 'lucide-react';
import { formatDateTime } from '../../lib/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
    name: '', description: '', start_date: '', end_date: '', closing_date: '',
    gender: 'both', min_age: '', max_age: '', cost: '', deposit: '',
    program_ids: [], class_ids: [], location: '', capacity: '',
    open_to_external: false, image_url: '',
};

const Activities = () => {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === 'super_admin';
    const [activities, setActivities] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [selectedActivity, setSelectedActivity] = useState(null);

    useEffect(() => { fetchAll(); }, []);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [a, p, c] = await Promise.all([
                axios.get(`${API_URL}/api/activities`, { withCredentials: true }),
                axios.get(`${API_URL}/api/programs`, { withCredentials: true }),
                axios.get(`${API_URL}/api/classes`, { withCredentials: true }),
            ]);
            setActivities(a.data || []);
            setPrograms(p.data || []);
            setClasses(c.data || []);
        } catch { toast.error('Failed to load activities'); }
        finally { setLoading(false); }
    };

    const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
    const openEdit = (a) => {
        setEditing(a);
        setForm({
            name: a.name || '', description: a.description || '',
            start_date: a.start_date || '', end_date: a.end_date || '', closing_date: a.closing_date || '',
            gender: a.gender || 'both', min_age: a.min_age ?? '', max_age: a.max_age ?? '',
            cost: a.cost ?? '', deposit: a.deposit ?? '',
            program_ids: a.program_ids || [], class_ids: a.class_ids || [],
            location: a.location || '', capacity: a.capacity ?? '',
            open_to_external: !!a.open_to_external,
            image_url: a.image_url || '',
        });
        setDialogOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.name || !form.start_date || !form.end_date || !form.closing_date || form.cost === '') {
            toast.error('Name, start/end/closing dates and cost are required'); return;
        }
        const payload = {
            name: form.name.trim(),
            description: form.description || '',
            start_date: form.start_date,
            end_date: form.end_date,
            closing_date: form.closing_date,
            gender: form.gender || 'both',
            min_age: form.min_age === '' ? null : Number(form.min_age),
            max_age: form.max_age === '' ? null : Number(form.max_age),
            cost: Number(form.cost),
            deposit: form.deposit === '' ? 0 : Number(form.deposit),
            program_ids: form.program_ids,
            class_ids: form.class_ids,
            location: form.location || '',
            capacity: form.capacity === '' ? null : Number(form.capacity),
            open_to_external: !!form.open_to_external,
            image_url: form.image_url || '',
        };
        setSaving(true);
        try {
            if (editing) {
                await axios.put(`${API_URL}/api/activities/${editing.id}`, payload, { withCredentials: true });
                toast.success('Activity updated');
            } else {
                const created = await axios.post(`${API_URL}/api/activities`, payload, { withCredentials: true });
                // If a pending image was selected before save, upload it now that we have an ID
                if (form._pendingImageFile && created.data?.id) {
                    await handleUploadImage(form._pendingImageFile, created.data.id);
                }
                toast.success('Activity created');
            }
            setDialogOpen(false);
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save');
        } finally { setSaving(false); }
    };

    const handleUploadImage = async (file, activityId) => {
        if (!file) return null;
        if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return null; }
        if (file.size > 4 * 1024 * 1024) { toast.error('Image is larger than 4 MB'); return null; }
        const fd = new FormData();
        fd.append('file', file);
        try {
            const r = await axios.post(`${API_URL}/api/activities/${activityId}/image`, fd, { withCredentials: true });
            return r.data?.image_url || null;
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to upload image');
            return null;
        }
    };

    const handleImageFileSelected = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        // If we're editing an existing activity, upload immediately so image_url is set on save
        if (editing?.id) {
            const url = await handleUploadImage(file, editing.id);
            if (url) { setForm(f => ({ ...f, image_url: url })); toast.success('Image uploaded'); }
        } else {
            // For new activity: keep the File on the form; upload right after create
            setForm(f => ({ ...f, _pendingImageFile: file, image_url: URL.createObjectURL(file) }));
        }
    };

    const handleDelete = async (a) => {
        if (!window.confirm(`Delete activity "${a.name}"? This will also fail if any active registrations exist.`)) return;
        try {
            await axios.delete(`${API_URL}/api/activities/${a.id}`, { withCredentials: true });
            toast.success('Activity deleted');
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete');
        }
    };

    const toggleArrayItem = (key, value) => {
        setForm(prev => {
            const list = prev[key] || [];
            return { ...prev, [key]: list.includes(value) ? list.filter(x => x !== value) : [...list, value] };
        });
    };

    if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#064E3B]" /></div></DashboardLayout>;

    // Shared dialog rendered once at the root so it works in BOTH list and detail views
    const editDialog = (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-['Outfit']">{editing ? 'Edit Activity' : 'New Activity'}</DialogTitle>
                    <DialogDescription>Set the details, eligibility filters, cost, and deposit (optional).</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSave} className="space-y-4">
                    <div className="space-y-2">
                        <Label>Name *</Label>
                        <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="activity-name" />
                    </div>
                    <div className="space-y-2">
                        <Label>Description</Label>
                        <textarea className="w-full min-h-[80px] rounded-md border border-[#D1D5DB] p-3 text-sm" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="activity-description" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-2">
                            <Label>Start date *</Label>
                            <Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} required data-testid="activity-start" />
                        </div>
                        <div className="space-y-2">
                            <Label>End date *</Label>
                            <Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} required data-testid="activity-end" />
                        </div>
                        <div className="space-y-2">
                            <Label>Closing date *</Label>
                            <Input type="date" value={form.closing_date} onChange={e => setForm({ ...form, closing_date: e.target.value })} required data-testid="activity-closing" />
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-2">
                            <Label>Cost (€) *</Label>
                            <Input type="number" step="0.01" min="0" value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })} required data-testid="activity-cost" />
                        </div>
                        <div className="space-y-2">
                            <Label>Deposit (€)</Label>
                            <Input type="number" step="0.01" min="0" value={form.deposit} onChange={e => setForm({ ...form, deposit: e.target.value })} placeholder="0 = no deposit" data-testid="activity-deposit" />
                        </div>
                        <div className="space-y-2">
                            <Label>Capacity</Label>
                            <Input type="number" min="0" value={form.capacity} onChange={e => setForm({ ...form, capacity: e.target.value })} placeholder="leave blank = unlimited" data-testid="activity-capacity" />
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-2">
                            <Label>Gender</Label>
                            <Select value={form.gender} onValueChange={v => setForm({ ...form, gender: v })}>
                                <SelectTrigger data-testid="activity-gender"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="both">Both</SelectItem>
                                    <SelectItem value="male">Male only</SelectItem>
                                    <SelectItem value="female">Female only</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Min age</Label>
                            <Input type="number" min="0" max="99" value={form.min_age} onChange={e => setForm({ ...form, min_age: e.target.value })} placeholder="—" data-testid="activity-min-age" />
                        </div>
                        <div className="space-y-2">
                            <Label>Max age</Label>
                            <Input type="number" min="0" max="99" value={form.max_age} onChange={e => setForm({ ...form, max_age: e.target.value })} placeholder="—" data-testid="activity-max-age" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>Location</Label>
                        <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="e.g. Dublin Branch" data-testid="activity-location" />
                    </div>

                    <div className="space-y-2 border border-[#FED7AA] bg-[#FFF7ED] rounded-md p-3">
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                className="mt-1"
                                checked={!!form.open_to_external}
                                onChange={e => setForm({ ...form, open_to_external: e.target.checked })}
                                data-testid="activity-open-to-external"
                            />
                            <span>
                                <span className="font-medium text-[#9A3412]">Open to external guardians</span>
                                <span className="block text-xs text-[#9A3412]/80 mt-0.5">
                                    Anyone (not just enrolled families) can sign up via the public page
                                    <code className="ml-1 bg-white/60 px-1 rounded">/public/activities</code>.
                                </span>
                            </span>
                        </label>
                    </div>

                    <div className="space-y-2">
                        <Label>Image <span className="text-xs text-[#6B7280] font-normal">(optional — shown on the public page and in the announcement email)</span></Label>
                        <div className="flex items-start gap-3">
                            {form.image_url ? (
                                <img
                                    src={form.image_url}
                                    alt="Activity"
                                    className="h-24 w-32 object-cover rounded border border-[#E5E7EB]"
                                    data-testid="activity-image-preview"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />
                            ) : (
                                <div className="h-24 w-32 rounded border border-dashed border-[#D1D5DB] bg-[#F9FAFB] flex items-center justify-center text-xs text-[#9CA3AF]">
                                    No image
                                </div>
                            )}
                            <div className="flex-1 space-y-2">
                                <Input
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    onChange={handleImageFileSelected}
                                    data-testid="activity-image-file-input"
                                    className="cursor-pointer"
                                />
                                {form.image_url && (
                                    <Button type="button" size="sm" variant="outline" onClick={() => setForm(f => ({ ...f, image_url: '', _pendingImageFile: undefined }))} data-testid="activity-image-clear">
                                        Remove image
                                    </Button>
                                )}
                                <p className="text-xs text-[#6B7280]">JPG / PNG / WebP · max 4 MB · shown at 240&times;140 on the public page.</p>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>Eligible programs <span className="text-xs text-[#6B7280] font-normal">— leave empty + classes empty to open to all students</span></Label>
                        <div className="border border-[#E5E7EB] rounded-md p-2 max-h-32 overflow-y-auto" data-testid="activity-program-list">
                            {programs.length === 0 && <p className="text-xs text-[#6B7280] p-2">No programs configured</p>}
                            {programs.map(p => (
                                <label key={p.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-[#F9FAFB] cursor-pointer text-sm">
                                    <input type="checkbox" checked={form.program_ids.includes(p.id)} onChange={() => toggleArrayItem('program_ids', p.id)} />
                                    <span>{p.name}</span>
                                    {p.location && <Badge variant="outline" className="text-xs ml-auto">{p.location}</Badge>}
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>Eligible specific classes <span className="text-xs text-[#6B7280] font-normal">— OR-ed with the programs above</span></Label>
                        <div className="border border-[#E5E7EB] rounded-md p-2 max-h-32 overflow-y-auto" data-testid="activity-class-list">
                            {classes.length === 0 && <p className="text-xs text-[#6B7280] p-2">No classes configured</p>}
                            {classes.map(c => (
                                <label key={c.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-[#F9FAFB] cursor-pointer text-sm">
                                    <input type="checkbox" checked={form.class_ids.includes(c.id)} onChange={() => toggleArrayItem('class_ids', c.id)} />
                                    <span className="font-mono text-xs">{c.code}</span>
                                    <span>{c.arabic_name || c.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button type="submit" disabled={saving} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="save-activity-btn">
                            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}{editing ? 'Update' : 'Create'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );

    if (selectedActivity) {
        return (
            <>
                <ActivityDetail
                    activity={selectedActivity}
                    isSuperAdmin={isSuperAdmin}
                    onBack={() => { setSelectedActivity(null); fetchAll(); }}
                    onEdit={() => openEdit(selectedActivity)}
                />
                {editDialog}
            </>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="admin-activities-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Activities</h1>
                        <p className="text-[#4B5563]">Camps, trips and one-off events with Stripe checkout</p>
                    </div>
                    <Button onClick={openCreate} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="add-activity-btn">
                        <Plus className="h-4 w-4 mr-2" />New Activity
                    </Button>
                </div>

                {activities.length === 0 ? (
                    <Card className="card-surface">
                        <CardContent className="flex flex-col items-center justify-center py-16">
                            <Calendar className="h-12 w-12 text-[#4B5563]/30 mb-4" />
                            <p className="text-[#4B5563] mb-4">No activities yet</p>
                            <Button onClick={openCreate} className="bg-[#064E3B] hover:bg-[#047857]"><Plus className="h-4 w-4 mr-2" />Create first activity</Button>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {activities.map(a => (
                            <ActivityCard
                                key={a.id}
                                activity={a}
                                onOpen={() => setSelectedActivity(a)}
                                onEdit={() => openEdit(a)}
                                onDelete={() => handleDelete(a)}
                                isSuperAdmin={isSuperAdmin}
                            />
                        ))}
                    </div>
                )}

                {editDialog}
            </div>
        </DashboardLayout>
    );
};

// ---- Activity Card ----
const ActivityCard = ({ activity, onOpen, onEdit, onDelete, isSuperAdmin }) => {
    const today = new Date().toISOString().slice(0, 10);
    const closing = activity.closing_date || '';
    const isClosed = closing && today > closing;
    const daysToClose = closing ? Math.ceil((new Date(closing) - new Date(today)) / (1000 * 60 * 60 * 24)) : null;
    let closingBadge = null;
    if (isClosed) closingBadge = <Badge className="bg-[#6B7280]">Closed</Badge>;
    else if (daysToClose !== null && daysToClose <= 3) closingBadge = <Badge className="bg-[#EF4444]"><AlertTriangle className="h-3 w-3 mr-1" />Closes in {daysToClose}d</Badge>;
    else if (daysToClose !== null && daysToClose <= 7) closingBadge = <Badge className="bg-[#F59E0B]"><AlertTriangle className="h-3 w-3 mr-1" />Closes in {daysToClose}d</Badge>;
    else closingBadge = <Badge variant="outline">Open</Badge>;

    return (
        <Card className="card-surface hover:shadow-md transition-shadow" data-testid={`activity-card-${activity.id}`}>
            <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-[#1F2937] text-base leading-tight">{activity.name}</h3>
                    {closingBadge}
                </div>
                {activity.description && <p className="text-xs text-[#4B5563] line-clamp-2">{activity.description}</p>}
                <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><span className="text-[#6B7280]">Dates:</span> <span className="tabular-nums">{activity.start_date} → {activity.end_date}</span></div>
                    <div><span className="text-[#6B7280]">Closing:</span> <span className="tabular-nums">{activity.closing_date || '—'}</span></div>
                    <div><span className="text-[#6B7280]">Cost:</span> <span className="font-bold text-[#064E3B]">€{Number(activity.cost || 0).toFixed(2)}</span></div>
                    <div><span className="text-[#6B7280]">Deposit:</span> <span>{activity.deposit ? `€${Number(activity.deposit).toFixed(2)}` : 'None'}</span></div>
                    {activity.location && <div className="col-span-2"><span className="text-[#6B7280]">Location:</span> {activity.location}</div>}
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {activity.gender && activity.gender !== 'both' && <Badge variant="outline" className="text-xs">{activity.gender}</Badge>}
                    {(activity.min_age != null || activity.max_age != null) && <Badge variant="outline" className="text-xs">{activity.min_age ?? '?'}–{activity.max_age ?? '?'}y</Badge>}
                    {activity.capacity ? <Badge variant="outline" className="text-xs">Cap {activity.capacity}</Badge> : null}
                    {activity.open_to_external ? <Badge className="bg-[#F59E0B] text-white text-xs" data-testid={`activity-public-badge-${activity.id}`}>Public</Badge> : null}
                </div>
                <div className="flex gap-2 pt-2 border-t border-[#F3F4F6]">
                    <Button size="sm" variant="outline" className="flex-1" onClick={onOpen} data-testid={`view-activity-${activity.id}`}><Eye className="h-3.5 w-3.5 mr-1" />Details</Button>
                    <Button size="sm" variant="outline" onClick={onEdit} data-testid={`edit-activity-${activity.id}`}><Edit className="h-3.5 w-3.5" /></Button>
                    {isSuperAdmin && (
                        <Button size="sm" variant="outline" className="text-[#EF4444] border-[#EF4444]" onClick={onDelete} data-testid={`delete-activity-${activity.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

// ---- Activity Detail (tabs) ----
// Client-side mirror of the backend _student_eligible_for_activity helper
const _ageAt = (dob, refDate) => {
    if (!dob) return null;
    try {
        const d = new Date(dob);
        const r = refDate ? new Date(refDate) : new Date();
        let yrs = r.getFullYear() - d.getFullYear();
        const m = r.getMonth() - d.getMonth();
        if (m < 0 || (m === 0 && r.getDate() < d.getDate())) yrs--;
        return yrs;
    } catch { return null; }
};
const _isEligible = (s, a) => {
    if (!s || s.status !== 'active') return false;
    const g = (a.gender || 'both').toLowerCase();
    if (g !== 'both' && (s.gender || '').toLowerCase() !== g) return false;
    if (a.min_age != null || a.max_age != null) {
        const age = _ageAt(s.date_of_birth, a.start_date);
        if (age == null) return false;
        if (a.min_age != null && age < Number(a.min_age)) return false;
        if (a.max_age != null && age > Number(a.max_age)) return false;
    }
    const progs = a.program_ids || [];
    const cls = a.class_ids || [];
    if (progs.length || cls.length) {
        if (progs.length && progs.includes(s.program_id)) return true;
        if (cls.length && cls.includes(s.class_id)) return true;
        return false;
    }
    return true;
};

const ActivityDetail = ({ activity, isSuperAdmin, onBack, onEdit }) => {
    const [tab, setTab] = useState('participants');
    const [participants, setParticipants] = useState([]);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refundDialog, setRefundDialog] = useState(null);
    const [refundReason, setRefundReason] = useState('');
    const [refunding, setRefunding] = useState(false);
    const [manualOpen, setManualOpen] = useState(false);
    const [manualSaving, setManualSaving] = useState(false);
    const [manualForm, setManualForm] = useState({ student_id: '', amount: '', method: 'cash', notes: '', created_at: '' });
    const [eligibleStudents, setEligibleStudents] = useState([]);
    const [methods, setMethods] = useState([]);
    const [sendingReminders, setSendingReminders] = useState(false);
    const [sendingPaymentReminders, setSendingPaymentReminders] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [addByCodeOpen, setAddByCodeOpen] = useState(false);
    const [addByCodeForm, setAddByCodeForm] = useState({ student_code: '', notes: '' });
    const [addByCodeSaving, setAddByCodeSaving] = useState(false);
    // Participants table controls (sort + inline edit + cancel + print)
    const [sortField, setSortField] = useState('student_name');
    const [sortDir, setSortDir] = useState('asc');
    const [editingCell, setEditingCell] = useState(null);
    const [cancelDialog, setCancelDialog] = useState(null);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelSaving, setCancelSaving] = useState(false);
    const [discountDialog, setDiscountDialog] = useState(null); // participant row
    const [discountForm, setDiscountForm] = useState({ amount: '', reason: '' });
    const [discountSaving, setDiscountSaving] = useState(false);

    const toggleSort = (field) => {
        if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortDir('asc'); }
    };
    const sortedParticipants = useMemo(() => {
        const arr = [...participants];
        const dir = sortDir === 'asc' ? 1 : -1;
        arr.sort((a, b) => {
            const va = (a[sortField] || '').toString().toLowerCase();
            const vb = (b[sortField] || '').toString().toLowerCase();
            if (va < vb) return -1 * dir;
            if (va > vb) return 1 * dir;
            return 0;
        });
        return arr;
    }, [participants, sortField, sortDir]);

    const saveInlineEdit = async (regId, field, value) => {
        try {
            await axios.patch(`${API_URL}/api/activities/${activity.id}/registrations/${regId}`, { [field]: value }, { withCredentials: true });
            setParticipants(prev => prev.map(p => p.id === regId ? { ...p, [field]: value || null } : p));
            setEditingCell(null);
        } catch (err) {
            toast.error(err.response?.data?.detail || `Failed to update ${field}`);
        }
    };

    const handleCancelSubmit = async () => {
        if (!cancelDialog) return;
        setCancelSaving(true);
        try {
            await axios.post(
                `${API_URL}/api/activities/${activity.id}/registrations/${cancelDialog.id}/cancel`,
                { reason: cancelReason },
                { withCredentials: true }
            );
            toast.success('Participation cancelled');
            setCancelDialog(null); setCancelReason('');
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to cancel');
        } finally { setCancelSaving(false); }
    };

    const openDiscountDialog = (p) => {
        setDiscountForm({
            amount: p.discount_applied > 0 ? String(p.discount_applied) : '',
            reason: p.discount_reason || '',
        });
        setDiscountDialog(p);
    };

    const handleDiscountSubmit = async () => {
        if (!discountDialog) return;
        const amt = Number(discountForm.amount || 0);
        if (Number.isNaN(amt) || amt < 0) { toast.error('Enter a non-negative amount'); return; }
        setDiscountSaving(true);
        try {
            const r = await axios.post(
                `${API_URL}/api/activities/${activity.id}/registrations/${discountDialog.id}/discount`,
                { amount: amt, reason: discountForm.reason },
                { withCredentials: true }
            );
            toast.success(r.data.message || (amt > 0 ? 'Discount applied' : 'Discount cleared'));
            setDiscountDialog(null);
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to apply discount');
        } finally { setDiscountSaving(false); }
    };

    const openPrintReport = (groupBy) => {
        const params = new URLSearchParams({ groupBy, sortField, sortDir });
        const basePath = window.location.pathname.startsWith('/manager') ? '/manager' : '/admin';
        window.open(`${basePath}/activities/${activity.id}/print?${params.toString()}`, '_blank', 'noopener');
    };

    useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [activity.id]);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [pa, pm, st, mt] = await Promise.all([
                axios.get(`${API_URL}/api/activities/${activity.id}/participants`, { withCredentials: true }),
                axios.get(`${API_URL}/api/activities/${activity.id}/payments`, { withCredentials: true }),
                axios.get(`${API_URL}/api/students?status=active`, { withCredentials: true }),
                axios.get(`${API_URL}/api/payment-methods`, { withCredentials: true }).catch(() => ({ data: [] })),
            ]);
            setParticipants(pa.data || []);
            setPayments(pm.data || []);
            // Eligible-only students list for the manual-payment dropdown
            const students = (st.data || []).filter(s => _isEligible(s, activity));
            setEligibleStudents(students);
            setMethods(mt.data || []);
        } catch { toast.error('Failed to load details'); }
        finally { setLoading(false); }
    };

    const totalCollected = useMemo(() => payments.reduce((s, p) => s + Number(p.amount || 0), 0), [payments]);
    const activeRegs = useMemo(() => participants.filter(p => p.status !== 'refunded' && p.status !== 'cancelled'), [participants]);
    const fullyPaid = activeRegs.filter(p => (p.amount_due ?? 0) <= 0).length;
    const deposited = activeRegs.filter(p => (p.amount_due ?? 0) > 0).length;

    const handleExport = async () => {
        setExporting(true);
        try {
            const res = await axios.get(`${API_URL}/api/activities/${activity.id}/export/excel`, { withCredentials: true, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `activity_${(activity.name || 'activity').replace(/\s+/g, '_')}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Excel downloaded');
        } catch { toast.error('Failed to export Excel'); }
        finally { setExporting(false); }
    };

    const handleSendReminders = async () => {
        setSendingReminders(true);
        try {
            // 1) Dry-run preview
            const preview = await axios.post(
                `${API_URL}/api/activities/${activity.id}/send-closing-reminders?dry_run=true`,
                {}, { withCredentials: true }
            );
            const { would_send = 0, skipped_dedup = 0, days_to_close } = preview.data || {};
            if (would_send === 0 && skipped_dedup === 0) {
                toast.info('No eligible-but-unregistered guardians to email.');
                return;
            }
            const skippedText = skipped_dedup ? ` (${skipped_dedup} skipped — already emailed in the last 12 hours)` : '';
            if (!window.confirm(`Send a reminder email to ${would_send} guardian${would_send === 1 ? '' : 's'}${skippedText}?\n\nActivity closes in ${days_to_close} day${days_to_close === 1 ? '' : 's'}. Continue?`)) return;

            // 2) Real send
            const r = await axios.post(`${API_URL}/api/activities/${activity.id}/send-closing-reminders`, {}, { withCredentials: true });
            const { sent, skipped_dedup: sk, errors = [] } = r.data || {};
            if (errors.length) {
                toast.warning(`Sent ${sent}, skipped ${sk}, ${errors.length} error(s)`);
            } else {
                toast.success(`${sent} reminder(s) sent${sk ? `, ${sk} skipped (already emailed)` : ''}.`);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to send reminders');
        } finally { setSendingReminders(false); }
    };

    const handleSendPaymentReminders = async () => {
        setSendingPaymentReminders(true);
        try {
            const preview = await axios.post(
                `${API_URL}/api/activities/${activity.id}/send-payment-reminders?dry_run=true`,
                {}, { withCredentials: true }
            );
            const { would_send = 0, skipped_dedup = 0, total_recipients = 0 } = preview.data || {};
            if (total_recipients === 0) {
                toast.info('No participants currently owe a balance.');
                return;
            }
            const skippedText = skipped_dedup ? ` (${skipped_dedup} skipped — emailed in the last 12 hours)` : '';
            if (would_send === 0) {
                toast.info(`All ${total_recipients} guardian(s) with a balance were emailed in the last 12 hours.`);
                return;
            }
            if (!window.confirm(`Send a payment-reminder email to ${would_send} guardian${would_send === 1 ? '' : 's'}${skippedText}?`)) return;

            const r = await axios.post(`${API_URL}/api/activities/${activity.id}/send-payment-reminders`, {}, { withCredentials: true });
            const { sent, skipped_dedup: sk, errors = [] } = r.data || {};
            if (errors.length) toast.warning(`Sent ${sent}, skipped ${sk}, ${errors.length} error(s)`);
            else toast.success(`${sent} payment reminder(s) sent${sk ? `, ${sk} skipped (already emailed)` : ''}.`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to send payment reminders');
        } finally { setSendingPaymentReminders(false); }
    };

    const openAddByCode = () => {
        setAddByCodeForm({ student_code: '', notes: '' });
        setAddByCodeOpen(true);
    };

    const handleAddByCodeSave = async (e) => {
        e.preventDefault();
        const code = (addByCodeForm.student_code || '').trim();
        if (!code) { toast.error('Please enter a student code'); return; }
        setAddByCodeSaving(true);
        try {
            const r = await axios.post(
                `${API_URL}/api/activities/${activity.id}/manual-register`,
                { student_code: code, notes: addByCodeForm.notes },
                { withCredentials: true }
            );
            toast.success(r.data.message || 'Student added');
            setAddByCodeOpen(false);
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to add student');
        } finally { setAddByCodeSaving(false); }
    };

    const openManual = () => {
        // Only show participants of THIS activity (not the whole student roster),
        // sorted alphabetically by student name.
        const rows = (participants || [])
            .filter(p => !['cancelled', 'refunded'].includes(p.status))
            .map(p => ({
                id: p.student_id,
                first_name: (p.student_name || '').split(' ')[0] || '',
                last_name: (p.student_name || '').split(' ').slice(1).join(' ') || '',
                reg_code: p.student_reg_code,
                display: p.student_name || '—',
            }))
            .sort((a, b) => a.display.localeCompare(b.display));
        setEligibleStudents(rows);
        setManualForm({ student_id: '', amount: '', method: 'cash', notes: '', created_at: '' });
        setManualOpen(true);
    };

    const handleManualSave = async (e) => {
        e.preventDefault();
        if (!manualForm.student_id || !manualForm.amount) {
            toast.error('Student and amount are required'); return;
        }
        setManualSaving(true);
        try {
            const payload = {
                student_id: manualForm.student_id,
                amount: Number(manualForm.amount),
                method: manualForm.method || 'cash',
                notes: manualForm.notes || '',
            };
            if (manualForm.created_at) payload.created_at = manualForm.created_at;
            const r = await axios.post(`${API_URL}/api/activities/${activity.id}/manual-payment`, payload, { withCredentials: true });
            toast.success(`Receipt ${r.data.receipt_number} recorded`);
            setManualOpen(false);
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to record payment');
        } finally { setManualSaving(false); }
    };

    const handleRefund = async () => {
        if (!refundDialog) return;
        setRefunding(true);
        try {
            const r = await axios.post(
                `${API_URL}/api/activities/${activity.id}/registrations/${refundDialog.id}/refund`,
                { reason: refundReason },
                { withCredentials: true }
            );
            toast.success(`Refunded €${(r.data.amount_refunded || 0).toFixed(2)}`);
            setRefundDialog(null);
            setRefundReason('');
            fetchAll();
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === 'string' ? d : (d?.message || 'Refund failed'));
        } finally { setRefunding(false); }
    };

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="activity-detail-page">
                <div>
                    <Button variant="ghost" size="sm" onClick={onBack} className="mb-2" data-testid="back-to-activities"><ArrowLeft className="h-4 w-4 mr-1" />Back to activities</Button>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div>
                            <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">{activity.name}</h1>
                            <p className="text-[#4B5563] text-sm">{activity.start_date} → {activity.end_date} • Closes {activity.closing_date || '—'} • €{Number(activity.cost || 0).toFixed(2)}{activity.deposit ? ` (deposit €${Number(activity.deposit).toFixed(2)})` : ''}</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button variant="outline" onClick={handleExport} disabled={exporting} data-testid="export-activity-excel-btn">
                                {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}Export Excel
                            </Button>
                            <Button variant="outline" onClick={openAddByCode} data-testid="add-student-by-code-btn">
                                <UserPlus className="h-4 w-4 mr-2" />Add student by code
                            </Button>
                            <Button variant="outline" onClick={handleSendReminders} disabled={sendingReminders} data-testid="send-closing-reminders-btn">
                                {sendingReminders ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}Send closing reminders
                            </Button>
                            <Button variant="outline" onClick={handleSendPaymentReminders} disabled={sendingPaymentReminders} data-testid="send-payment-reminders-btn">
                                {sendingPaymentReminders ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}Send payment reminders
                            </Button>
                            <Button variant="outline" onClick={openManual} data-testid="add-manual-payment-btn">
                                <Wallet className="h-4 w-4 mr-2" />Add manual payment
                            </Button>
                            <Button variant="outline" onClick={() => openPrintReport('group')} data-testid="print-by-group-btn">
                                <Printer className="h-4 w-4 mr-2" />Print by group
                            </Button>
                            <Button variant="outline" onClick={() => openPrintReport('room')} data-testid="print-by-room-btn">
                                <Printer className="h-4 w-4 mr-2" />Print by room
                            </Button>
                            <Button variant="outline" onClick={onEdit} data-testid="edit-from-detail-btn"><Edit className="h-4 w-4 mr-2" />Edit activity</Button>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Card className="card-surface"><CardContent className="p-4"><p className="text-xs text-[#6B7280]">Active registrations</p><p className="text-2xl font-bold text-[#064E3B] tabular-nums">{activeRegs.length}</p></CardContent></Card>
                    <Card className="card-surface"><CardContent className="p-4"><p className="text-xs text-[#6B7280]">Fully paid</p><p className="text-2xl font-bold text-[#10B981] tabular-nums">{fullyPaid}</p></CardContent></Card>
                    <Card className="card-surface"><CardContent className="p-4"><p className="text-xs text-[#6B7280]">Deposit only</p><p className="text-2xl font-bold text-[#F59E0B] tabular-nums">{deposited}</p></CardContent></Card>
                    <Card className="card-surface"><CardContent className="p-4"><p className="text-xs text-[#6B7280]">Total collected</p><p className="text-2xl font-bold text-[#1F2937] tabular-nums">€{totalCollected.toFixed(2)}</p></CardContent></Card>
                </div>

                <div className="inline-flex bg-[#F3F4F6] rounded-lg p-1 gap-1">
                    <button onClick={() => setTab('participants')} data-testid="tab-participants" className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 ${tab === 'participants' ? 'bg-white shadow-sm text-[#064E3B]' : 'text-[#4B5563]'}`}>
                        <Users className="h-4 w-4" />Participants ({participants.length})
                    </button>
                    <button onClick={() => setTab('payments')} data-testid="tab-payments" className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 ${tab === 'payments' ? 'bg-white shadow-sm text-[#064E3B]' : 'text-[#4B5563]'}`}>
                        <Receipt className="h-4 w-4" />Payments ({payments.length})
                    </button>
                </div>

                {loading ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#064E3B]" /></div> : (
                    <Card className="card-surface">
                        <CardContent className="p-0">
                            {tab === 'participants' ? (
                                participants.length === 0 ? <div className="py-12 text-center text-[#6B7280]">No registrations yet.</div> :
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader><TableRow>
                                            <SortableHead label="Student" field="student_name" sortField={sortField} sortDir={sortDir} onClick={toggleSort} />
                                            <SortableHead label="DOB" field="student_dob" sortField={sortField} sortDir={sortDir} onClick={toggleSort} />
                                            <TableHead>Guardian</TableHead>
                                            <TableHead>Mobile</TableHead>
                                            <SortableHead label="Group" field="group" sortField={sortField} sortDir={sortDir} onClick={toggleSort} />
                                            <SortableHead label="Room" field="room" sortField={sortField} sortDir={sortDir} onClick={toggleSort} />
                                            <TableHead className="text-right">Paid</TableHead>
                                            <TableHead className="text-right">Due</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow></TableHeader>
                                        <TableBody>
                                            {sortedParticipants.map(p => (
                                                <TableRow key={p.id} data-testid={`participant-${p.id}`}>
                                                    <TableCell><p className="font-medium">{p.student_name}</p><p className="text-xs text-[#6B7280] tabular-nums">{p.student_reg_code}</p></TableCell>
                                                    <TableCell className="text-xs text-[#4B5563] tabular-nums">{p.student_dob || <span className="text-[#9CA3AF]">—</span>}</TableCell>
                                                    <TableCell><p>{p.parent_name}</p><p className="text-xs text-[#6B7280]">{p.parent_email}</p></TableCell>
                                                    <TableCell className="text-xs tabular-nums">
                                                        {p.parent_mobile ? (
                                                            <a href={`tel:${p.parent_mobile}`} className="text-[#064E3B] hover:underline">{p.parent_mobile}</a>
                                                        ) : <span className="text-[#9CA3AF]">—</span>}
                                                        {p.guardian2_mobile ? (
                                                            <div className="text-[10px] text-[#9CA3AF] mt-0.5">G2: <a href={`tel:${p.guardian2_mobile}`} className="text-[#4B5563] hover:underline">{p.guardian2_mobile}</a></div>
                                                        ) : null}
                                                    </TableCell>
                                                    <TableCell>
                                                        <InlineEdit
                                                            regId={p.id}
                                                            field="group"
                                                            value={p.group}
                                                            editing={editingCell?.regId === p.id && editingCell?.field === 'group'}
                                                            onEdit={setEditingCell}
                                                            onSave={saveInlineEdit}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <InlineEdit
                                                            regId={p.id}
                                                            field="room"
                                                            value={p.room}
                                                            editing={editingCell?.regId === p.id && editingCell?.field === 'room'}
                                                            onEdit={setEditingCell}
                                                            onSave={saveInlineEdit}
                                                        />
                                                    </TableCell>
                                                    <TableCell className="text-right tabular-nums font-medium text-[#10B981]">€{Number(p.amount_paid || 0).toFixed(2)}</TableCell>
                                                    <TableCell className="text-right tabular-nums">
                                                        {Number(p.amount_due || 0) > 0
                                                            ? <span className="text-[#EF4444] font-medium">€{Number(p.amount_due).toFixed(2)}</span>
                                                            : <span className="text-[#10B981]">—</span>}
                                                        {Number(p.discount_applied || 0) > 0 && (
                                                            <div className="text-[10px] text-[#0369A1] mt-0.5" data-testid={`discount-chip-${p.id}`}>
                                                                Discount: €{Number(p.discount_applied).toFixed(2)}
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {p.status === 'paid' && <Badge className="bg-[#10B981]">Paid</Badge>}
                                                        {p.status === 'active' && Number(p.amount_paid || 0) > 0 && <Badge className="bg-[#F59E0B]">Deposit</Badge>}
                                                        {p.status === 'active' && Number(p.amount_paid || 0) === 0 && <Badge className="bg-[#EF4444]">Unpaid</Badge>}
                                                        {p.status === 'refunded' && <Badge className="bg-[#6B7280]">Refunded</Badge>}
                                                        {p.status === 'cancelled' && <Badge variant="outline">Cancelled</Badge>}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex justify-end gap-1">
                                                            {p.status !== 'cancelled' && p.status !== 'refunded' && (
                                                                <Button size="sm" variant="ghost" className="text-[#0369A1] h-8" onClick={() => openDiscountDialog(p)} data-testid={`apply-discount-${p.id}`}>
                                                                    Discount
                                                                </Button>
                                                            )}
                                                            {p.status !== 'cancelled' && p.status !== 'refunded' && (
                                                                <Button size="sm" variant="ghost" className="text-[#F59E0B] h-8" onClick={() => { setCancelDialog(p); setCancelReason(''); }} data-testid={`cancel-participation-${p.id}`}>
                                                                    Cancel
                                                                </Button>
                                                            )}
                                                            {isSuperAdmin && p.status !== 'refunded' && p.status !== 'cancelled' && Number(p.amount_paid || 0) > 0 && (
                                                                <Button size="sm" variant="outline" className="text-[#EF4444] border-[#EF4444] h-8" onClick={() => { setRefundDialog(p); setRefundReason(''); }} data-testid={`refund-${p.id}`}>
                                                                    <RotateCcw className="h-3 w-3 mr-1" />Refund
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            ) : (
                                payments.length === 0 ? <div className="py-12 text-center text-[#6B7280]">No payments yet.</div> :
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader><TableRow>
                                            <TableHead>Receipt #</TableHead>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Guardian</TableHead>
                                            <TableHead>Student</TableHead>
                                            <TableHead>Phase</TableHead>
                                            <TableHead className="text-right">Amount</TableHead>
                                        </TableRow></TableHeader>
                                        <TableBody>
                                            {payments.map(p => (
                                                <TableRow key={p.id}>
                                                    <TableCell className="font-mono text-xs">{p.receipt_number}</TableCell>
                                                    <TableCell className="text-xs tabular-nums">{formatDateTime(p.created_at)}</TableCell>
                                                    <TableCell><p className="text-sm">{p.parent_name}</p><p className="text-xs text-[#6B7280] tabular-nums">{p.parent_reg_code}</p></TableCell>
                                                    <TableCell className="text-sm">{p.activity_student_name}</TableCell>
                                                    <TableCell><Badge variant="outline" className="text-xs capitalize">{p.activity_phase || 'full'}</Badge></TableCell>
                                                    <TableCell className="text-right font-bold tabular-nums">€{Number(p.amount || 0).toFixed(2)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                <Dialog open={!!refundDialog} onOpenChange={(o) => !o && setRefundDialog(null)}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="font-['Outfit'] text-[#EF4444]">Refund registration</DialogTitle>
                            <DialogDescription>
                                This will issue a real Stripe refund for €{Number(refundDialog?.amount_paid || 0).toFixed(2)} and mark the registration as refunded.
                            </DialogDescription>
                        </DialogHeader>
                        {refundDialog && (
                            <div className="space-y-3 text-sm">
                                <div className="bg-[#FAF9F6] rounded p-3">
                                    <p><span className="text-[#6B7280]">Student:</span> <span className="font-medium">{refundDialog.student_name}</span></p>
                                    <p><span className="text-[#6B7280]">Guardian:</span> <span className="font-medium">{refundDialog.parent_name}</span></p>
                                    <p><span className="text-[#6B7280]">Paid so far:</span> <span className="font-bold text-[#10B981]">€{Number(refundDialog.amount_paid || 0).toFixed(2)}</span></p>
                                </div>
                                <div className="space-y-2">
                                    <Label>Reason (optional)</Label>
                                    <Input value={refundReason} onChange={e => setRefundReason(e.target.value)} placeholder="e.g. Activity cancelled" data-testid="refund-reason" />
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setRefundDialog(null)}>Cancel</Button>
                            <Button onClick={handleRefund} disabled={refunding} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="confirm-refund-btn">
                                {refunding && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Confirm refund
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                <Dialog open={manualOpen} onOpenChange={setManualOpen}>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle className="font-['Outfit']">Record manual payment</DialogTitle>
                            <DialogDescription>Cash, bank transfer or other non-Stripe payment. Generates a NHQ-ACT-#### receipt and emails the guardian.</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleManualSave} className="space-y-4">
                            <div className="space-y-2">
                                <Label>Student *</Label>
                                <Select value={manualForm.student_id} onValueChange={v => setManualForm({ ...manualForm, student_id: v })}>
                                    <SelectTrigger data-testid="manual-student"><SelectValue placeholder={eligibleStudents.length ? 'Select student' : 'No eligible students'} /></SelectTrigger>
                                    <SelectContent>
                                        {eligibleStudents.map(s => (
                                            <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} <span className="text-xs text-[#6B7280]">({s.reg_code})</span></SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {!eligibleStudents.length && <p className="text-xs text-[#EF4444]">No participants on this activity yet. Register students first.</p>}
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label>Amount (€) *</Label>
                                    <Input type="number" step="0.01" min="0.01" value={manualForm.amount} onChange={e => setManualForm({ ...manualForm, amount: e.target.value })} required data-testid="manual-amount" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Method</Label>
                                    <Select value={manualForm.method} onValueChange={v => setManualForm({ ...manualForm, method: v })}>
                                        <SelectTrigger data-testid="manual-method"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {(methods.length ? methods : [{ id: 'cash', name: 'cash' }, { id: 'bank_transfer', name: 'bank_transfer' }]).map(m => (
                                                <SelectItem key={m.id} value={m.name || m.id}>{(m.name || m.id).replace(/_/g, ' ')}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Receipt date <span className="text-xs text-[#6B7280] font-normal">— leave blank for today</span></Label>
                                <Input type="date" value={manualForm.created_at} onChange={e => setManualForm({ ...manualForm, created_at: e.target.value })} data-testid="manual-date" />
                            </div>
                            <div className="space-y-2">
                                <Label>Notes</Label>
                                <Input value={manualForm.notes} onChange={e => setManualForm({ ...manualForm, notes: e.target.value })} placeholder="optional" data-testid="manual-notes" />
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setManualOpen(false)}>Cancel</Button>
                                <Button type="submit" disabled={manualSaving || !eligibleStudents.length} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="save-manual-payment-btn">
                                    {manualSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Record payment
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                <Dialog open={addByCodeOpen} onOpenChange={setAddByCodeOpen}>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle className="font-['Outfit']">Add student to activity by code</DialogTitle>
                            <DialogDescription>
                                Type a student&rsquo;s registration code to add them directly — works for active, waiting-list, unregistered or suspended students.
                                The new registration starts unpaid; use <em>Add manual payment</em> after saving to record any payment.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleAddByCodeSave} className="space-y-4">
                            <div className="space-y-2">
                                <Label>Student code *</Label>
                                <Input
                                    value={addByCodeForm.student_code}
                                    onChange={e => setAddByCodeForm({ ...addByCodeForm, student_code: e.target.value })}
                                    placeholder="e.g. NHQ-1234"
                                    required
                                    autoFocus
                                    data-testid="add-by-code-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Internal note <span className="text-xs text-[#6B7280] font-normal">(optional)</span></Label>
                                <Input
                                    value={addByCodeForm.notes}
                                    onChange={e => setAddByCodeForm({ ...addByCodeForm, notes: e.target.value })}
                                    placeholder="e.g. Added on behalf of guardian via phone"
                                    data-testid="add-by-code-notes"
                                />
                            </div>
                            <div className="text-xs text-[#9A3412] bg-[#FFF7ED] border border-[#FED7AA] rounded p-3">
                                Note: this bypasses eligibility filters (age, gender, program). Double-check the student matches the activity before saving.
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setAddByCodeOpen(false)}>Cancel</Button>
                                <Button type="submit" disabled={addByCodeSaving} className="bg-[#064E3B] hover:bg-[#047857] text-white" data-testid="save-add-by-code-btn">
                                    {addByCodeSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Add to activity
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                <Dialog open={!!cancelDialog} onOpenChange={(o) => !o && setCancelDialog(null)}>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle className="font-['Outfit']">Cancel participation</DialogTitle>
                            <DialogDescription>
                                Cancels the registration and frees the spot. This does <strong>not</strong> refund any payment already made — use the Refund button for that.
                            </DialogDescription>
                        </DialogHeader>
                        {cancelDialog && (
                            <div className="space-y-3">
                                <div className="bg-[#FAF9F6] rounded p-3 text-sm">
                                    <p><span className="text-[#6B7280]">Student:</span> <span className="font-medium">{cancelDialog.student_name}</span></p>
                                    <p><span className="text-[#6B7280]">Guardian:</span> {cancelDialog.parent_name}</p>
                                    {Number(cancelDialog.amount_paid || 0) > 0 && (
                                        <p className="text-xs text-[#F59E0B] mt-1">Note: €{Number(cancelDialog.amount_paid).toFixed(2)} has been paid — remember to refund separately if needed.</p>
                                    )}
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Reason <span className="text-xs text-[#6B7280] font-normal">(optional)</span></Label>
                                    <Input value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="e.g. Guardian requested cancellation" data-testid="cancel-reason-input" />
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setCancelDialog(null)}>Keep</Button>
                            <Button className="bg-[#EF4444] hover:bg-[#DC2626] text-white" onClick={handleCancelSubmit} disabled={cancelSaving} data-testid="confirm-cancel-participation">
                                {cancelSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Cancel participation
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                <Dialog open={!!discountDialog} onOpenChange={(o) => !o && setDiscountDialog(null)}>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle className="font-['Outfit']">Apply activity discount</DialogTitle>
                            <DialogDescription>
                                Reduces the remaining balance for <strong>this activity only</strong> — the guardian&rsquo;s general fees are not affected.
                                Enter 0 to clear an existing discount.
                            </DialogDescription>
                        </DialogHeader>
                        {discountDialog && (
                            <div className="space-y-3">
                                <div className="bg-[#FAF9F6] rounded p-3 text-sm space-y-1">
                                    <p><span className="text-[#6B7280]">Student:</span> <span className="font-medium">{discountDialog.student_name}</span></p>
                                    <p><span className="text-[#6B7280]">Activity cost:</span> €{Number(activity.cost || 0).toFixed(2)}</p>
                                    <p><span className="text-[#6B7280]">Paid so far:</span> €{Number(discountDialog.amount_paid || 0).toFixed(2)}</p>
                                    <p><span className="text-[#6B7280]">Currently due:</span> <span className="font-medium text-[#EF4444]">€{Number(discountDialog.amount_due || 0).toFixed(2)}</span></p>
                                    {Number(discountDialog.discount_applied || 0) > 0 && (
                                        <p className="text-xs text-[#0369A1]">Existing discount: €{Number(discountDialog.discount_applied).toFixed(2)}{discountDialog.discount_reason ? ` — ${discountDialog.discount_reason}` : ''}</p>
                                    )}
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Discount amount (€) *</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        max={Number(activity.cost || 0) - Number(discountDialog.amount_paid || 0)}
                                        value={discountForm.amount}
                                        onChange={(e) => setDiscountForm(f => ({ ...f, amount: e.target.value }))}
                                        placeholder="0.00"
                                        data-testid="discount-amount-input"
                                    />
                                    <p className="text-xs text-[#6B7280]">
                                        Max: €{(Number(activity.cost || 0) - Number(discountDialog.amount_paid || 0)).toFixed(2)} (remaining balance)
                                    </p>
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Reason <span className="text-xs text-[#6B7280] font-normal">(optional)</span></Label>
                                    <Input
                                        value={discountForm.reason}
                                        onChange={(e) => setDiscountForm(f => ({ ...f, reason: e.target.value }))}
                                        placeholder="e.g. Sibling discount, hardship, promotion"
                                        maxLength={200}
                                        data-testid="discount-reason-input"
                                    />
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDiscountDialog(null)}>Cancel</Button>
                            <Button
                                className="bg-[#064E3B] hover:bg-[#047857] text-white"
                                onClick={handleDiscountSubmit}
                                disabled={discountSaving}
                                data-testid="confirm-discount"
                            >
                                {discountSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                {Number(discountForm.amount || 0) > 0 ? 'Apply discount' : 'Clear discount'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
};

// ---- Sortable table header ----
const SortableHead = ({ label, field, sortField, sortDir, onClick }) => {
    const active = sortField === field;
    const Icon = !active ? ChevronsUpDown : (sortDir === 'asc' ? ArrowUp : ArrowDown);
    return (
        <TableHead>
            <button type="button" onClick={() => onClick(field)} className="inline-flex items-center gap-1 hover:text-[#064E3B]" data-testid={`sort-${field}`}>
                {label} <Icon className={`h-3.5 w-3.5 ${active ? 'text-[#064E3B]' : 'text-[#9CA3AF]'}`} />
            </button>
        </TableHead>
    );
};

// ---- Inline text editor (click to edit, Enter to save, Esc to cancel) ----
const InlineEdit = ({ regId, field, value, editing, onEdit, onSave }) => {
    const [draft, setDraft] = React.useState(value || '');
    React.useEffect(() => { setDraft(value || ''); }, [value, editing]);
    if (editing) {
        return (
            <div className="flex items-center gap-1">
                <Input
                    autoFocus
                    value={draft}
                    maxLength={60}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') onSave(regId, field, draft);
                        else if (e.key === 'Escape') onEdit(null);
                    }}
                    className="h-8 text-sm"
                    data-testid={`inline-input-${field}-${regId}`}
                />
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-[#10B981]" onClick={() => onSave(regId, field, draft)}>✓</Button>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-[#6B7280]" onClick={() => onEdit(null)}><X className="h-3.5 w-3.5" /></Button>
            </div>
        );
    }
    return (
        <button
            type="button"
            className="inline-flex text-sm px-2 py-0.5 rounded hover:bg-[#F3F4F6] min-w-[3rem] text-left"
            onClick={() => onEdit({ regId, field, value: value || '' })}
            data-testid={`inline-cell-${field}-${regId}`}
        >
            {value || <span className="text-[#9CA3AF] italic">—</span>}
        </button>
    );
};

export default Activities;
